/**
 * Admin → DSGVO-Doku-Pflichten (Art. 30 VVT, TOMs, Breach, TIA/SCC).
 * Ausfüllbare Formulare + PDF-Export + Aufbewahrungs-/TSE-Hinweise + Consent-Log.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Download, Loader2, Save, ShieldCheck, Archive, ListChecks, ChevronLeft } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";

export default function AdminDokuPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [docs, setDocs] = useState([]);
    const [tse, setTse] = useState(null);
    const [consent, setConsent] = useState(null);
    const [active, setActive] = useState(null); // full doc being edited
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!authLoading && user?.role !== "admin") navigate("/");
    }, [user, authLoading, navigate]);

    const loadOverview = async () => {
        setLoading(true);
        try {
            const [d, t, c] = await Promise.all([
                api.get("/admin/doku"),
                api.get("/admin/doku/tse-hinweise"),
                api.get("/admin/consent-log", { params: { limit: 50 } }),
            ]);
            setDocs(d.data.documents || []);
            setTse(t.data);
            setConsent(c.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
        } finally { setLoading(false); }
    };

    useEffect(() => { if (user?.role === "admin") loadOverview(); }, [user]);

    const openDoc = async (key) => {
        try {
            const { data } = await api.get(`/admin/doku/${key}`);
            setActive(data);
        } catch (e) { toast.error("Dokument konnte nicht geladen werden"); }
    };

    const updateSection = (idx, field, value) => {
        setActive((a) => ({
            ...a,
            sections: a.sections.map((s, i) => (i === idx ? { ...s, [field]: value } : s)),
        }));
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/doku/${active.key}`, {
                sections: active.sections.map((s) => ({ title: s.title || "", content: s.content || "" })),
                title: active.title,
            });
            toast.success("Gespeichert");
            await loadOverview();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setSaving(false); }
    };

    const downloadPdf = async (key) => {
        try {
            const r = await api.get(`/admin/doku/${key}.pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `doku-${key}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error("PDF-Export fehlgeschlagen"); }
    };

    if (authLoading || loading) {
        return <div className="min-h-screen bg-white flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#FFCC00]" /></div>;
    }

    // ---- Editor view ----
    if (active) {
        return (
            <div className="min-h-screen bg-white" data-testid="admin-doku-editor">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
                    <button onClick={() => setActive(null)}
                        className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#6B7280] hover:text-[#111827] mb-4"
                        data-testid="doku-back">
                        <ChevronLeft className="w-4 h-4" /> Zurück zur Übersicht
                    </button>
                    <div className="flex items-center gap-3 mb-1">
                        <FileText className="w-6 h-6 text-[#FFCC00]" />
                        <h1 className="text-3xl font-black text-[#111827] tracking-tight">{active.title}</h1>
                    </div>
                    <p className="text-sm text-[#6B7280] mb-6">{active.subtitle}</p>

                    <div className="space-y-5">
                        {active.sections.map((s, i) => (
                            <div key={i} className="border-2 border-gray-200 rounded-sm p-4" data-testid={`doku-section-${i}`}>
                                <Input value={s.title} onChange={(e) => updateSection(i, "title", e.target.value)}
                                    className="font-bold border-0 border-b-2 rounded-none px-0 mb-2 focus-visible:ring-0"
                                    data-testid={`doku-section-title-${i}`} />
                                <Textarea value={s.content} onChange={(e) => updateSection(i, "content", e.target.value)}
                                    rows={3} placeholder="Inhalt eintragen…"
                                    className="rounded-sm border-2 text-sm" data-testid={`doku-section-content-${i}`} />
                            </div>
                        ))}
                    </div>

                    <div className="sticky bottom-0 bg-white border-t-2 border-[#111827] mt-6 py-4 flex gap-3">
                        <Button onClick={save} disabled={saving}
                            className="bg-[#111827] hover:bg-black text-white rounded-sm font-bold uppercase text-xs tracking-wider"
                            data-testid="doku-save">
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Speichern
                        </Button>
                        <Button onClick={() => downloadPdf(active.key)} variant="outline"
                            className="rounded-sm border-2 border-[#111827] font-bold uppercase text-xs tracking-wider"
                            data-testid="doku-pdf">
                            <Download className="w-4 h-4 mr-2" /> Als PDF
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ---- Overview ----
    return (
        <div className="min-h-screen bg-white" data-testid="admin-doku-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                <Link to="/admin/compliance" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#6B7280] hover:text-[#111827]" data-testid="doku-back-compliance">
                    <ChevronLeft className="w-4 h-4" /> Compliance
                </Link>
                <div className="flex items-center gap-3 mt-3 mb-2">
                    <ShieldCheck className="w-7 h-7 text-[#FFCC00]" />
                    <h1 className="text-4xl font-black text-[#111827] tracking-tight">Datenschutz-Doku</h1>
                </div>
                <p className="text-sm text-[#6B7280] mb-8 max-w-3xl">
                    Ausfüllbare DSGVO-Pflichtdokumente — Art. 30 (VVT), Art. 32 (TOMs), Art. 33/34 (Breach)
                    und Kapitel V (Drittland/SCC). Bearbeiten, speichern und als PDF exportieren.
                </p>

                {/* Doc cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10" data-testid="doku-cards">
                    {docs.map((d) => (
                        <div key={d.key} className="border-2 border-[#111827] rounded-sm p-5 flex flex-col" data-testid={`doku-card-${d.key}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <h2 className="font-black text-lg text-[#111827]">{d.title}</h2>
                                    <p className="text-xs text-[#6B7280] mt-0.5">{d.subtitle}</p>
                                </div>
                                <Badge className={`${d.complete ? "bg-green-600" : "bg-amber-500"} text-white font-bold text-[10px] uppercase`}>
                                    {d.sections_filled}/{d.sections_total}
                                </Badge>
                            </div>
                            <div className="flex gap-2 mt-4">
                                <Button onClick={() => openDoc(d.key)}
                                    className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] rounded-sm font-bold uppercase text-xs tracking-wider flex-1"
                                    data-testid={`doku-edit-${d.key}`}>
                                    <FileText className="w-4 h-4 mr-1" /> Bearbeiten
                                </Button>
                                <Button onClick={() => downloadPdf(d.key)} variant="outline"
                                    className="rounded-sm border-2 border-[#111827] font-bold uppercase text-xs tracking-wider"
                                    data-testid={`doku-pdf-${d.key}`}>
                                    <Download className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Aufbewahrung / TSE */}
                <section className="border-2 border-gray-200 rounded-sm p-5 mb-8" data-testid="tse-info">
                    <div className="flex items-center justify-between gap-2 mb-4">
                        <div className="flex items-center gap-2">
                            <Archive className="w-5 h-5 text-[#111827]" />
                            <h2 className="font-black text-lg uppercase tracking-wider">Aufbewahrung & TSE-Hinweise</h2>
                        </div>
                        <Link to="/admin/gobd" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#111827] border-2 border-[#111827] rounded-sm px-3 py-1.5 hover:bg-[#FFCC00]" data-testid="gobd-archive-link">
                            GoBD-Archiv öffnen
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Aufbewahrungsfristen</h3>
                            <ul className="space-y-2">
                                {(tse?.aufbewahrung || []).map((r, i) => (
                                    <li key={i} className="text-sm flex justify-between gap-3 border-b border-gray-100 pb-1">
                                        <span className="text-[#374151]">{r.kategorie}</span>
                                        <span className="font-bold text-[#111827] whitespace-nowrap">{r.frist}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">TSE / KassenSichV</h3>
                            <ul className="space-y-2 list-disc pl-4">
                                {(tse?.tse || []).map((t, i) => (
                                    <li key={i} className="text-sm text-[#374151]">{t}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Consent-Log */}
                <section className="border-2 border-gray-200 rounded-sm p-5" data-testid="consent-log">
                    <div className="flex items-center gap-2 mb-4">
                        <ListChecks className="w-5 h-5 text-[#111827]" />
                        <h2 className="font-black text-lg uppercase tracking-wider">Werbe-Einwilligungen (Consent-Log)</h2>
                    </div>
                    <div className="flex gap-3 mb-4 text-xs">
                        <Badge className="bg-green-600 text-white">Erteilt: {consent?.counts?.granted ?? 0}</Badge>
                        <Badge className="bg-red-600 text-white">Widerrufen: {consent?.counts?.withdrawn ?? 0}</Badge>
                        <Badge variant="outline">Angefragt: {consent?.counts?.requested ?? 0}</Badge>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-gray-500 border-b">
                                    <th className="py-1 pr-3">Zeitpunkt</th>
                                    <th className="py-1 pr-3">Aktion</th>
                                    <th className="py-1 pr-3">Quelle</th>
                                    <th className="py-1 pr-3">Kanäle</th>
                                    <th className="py-1 pr-3">Händler</th>
                                    <th className="py-1 pr-3">IP</th>
                                </tr>
                            </thead>
                            <tbody data-testid="consent-log-rows">
                                {(consent?.items || []).map((it) => (
                                    <tr key={it.id} className="border-b border-gray-100">
                                        <td className="py-1 pr-3 whitespace-nowrap">{(it.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                        <td className="py-1 pr-3">
                                            <span className={`font-bold ${it.action === "granted" ? "text-green-700" : it.action === "withdrawn" ? "text-red-700" : "text-gray-600"}`}>
                                                {it.action === "granted" ? "erteilt" : it.action === "withdrawn" ? "widerrufen" : "angefragt"}
                                            </span>
                                        </td>
                                        <td className="py-1 pr-3">{it.source}</td>
                                        <td className="py-1 pr-3">{(it.channels || []).join(", ")}</td>
                                        <td className="py-1 pr-3">{it.dealer_name || "—"}</td>
                                        <td className="py-1 pr-3 text-gray-400">{it.ip || "—"}</td>
                                    </tr>
                                ))}
                                {(!consent?.items || consent.items.length === 0) && (
                                    <tr><td colSpan={6} className="py-4 text-center text-gray-400">Noch keine Einträge.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </div>
    );
}
